import { usePostDocumentsMutation } from "src/core/api/documents";
import {
  useCreateOrReplaceExternalDocumentMutation,
  useDeleteDocumentMutation,
  useDeleteExternalDocumentMutation,
} from "src/core/gql/graphql";
import { Document } from "src/core/types/document";
import { FolderEntrepriseFormValues } from "src/core/types/folder";
import { toIsoUtcFromDate } from "src/core/utils/date";

export type PostDocumentsFn = ReturnType<typeof usePostDocumentsMutation>[0];
export type DeleteDocumentFn = ReturnType<typeof useDeleteDocumentMutation>[0];
export type DeleteExternalDocumentFn = ReturnType<typeof useDeleteExternalDocumentMutation>[0];
export type CreateOrReplaceExternalDocumentFn = ReturnType<
  typeof useCreateOrReplaceExternalDocumentMutation
>[0];

type DocumentsMap = FolderEntrepriseFormValues["documents"];
type DefaultDocsMap = Record<string, Document>;
type PostResult = Awaited<ReturnType<PostDocumentsFn>>;
type ExternalDocumentResult = Awaited<ReturnType<CreateOrReplaceExternalDocumentFn>>;
type UploadResult = PostResult | ExternalDocumentResult;

type UploadSuccess = {
  ok: true;
  /** Code du type de document (ex: "kbis") */
  code: string;
};

type UploadError = {
  ok: false;
  /** Code du type de document (ex: "kbis") */
  code: string;
  /** Erreur renvoyée par l'API */
  error: unknown;
};

export type UploadDocumentResult = UploadSuccess | UploadError;

/**
 * Upload “safe” d'un document via l'endpoint documents (multipart).
 *
 * - Ne throw jamais : en cas d'erreur (RTK Query / réseau), retourne `{ ok: false, error }`.
 * - À utiliser dans les formulaires où on veut afficher un retour précis par document
 *   sans faire échouer toute la sauvegarde.
 */
const safeUploadDocument = async (params: {
  doc: Document;
  code: string;
  corporationId: string;
  postDocuments: PostDocumentsFn;
}): Promise<UploadDocumentResult> => {
  const { doc, code, corporationId, postDocuments } = params;

  try {
    const formData = new FormData();
    formData.append("file", doc.file!);

    const result = await postDocuments({
      documentTypeCode: code,
      data: formData,
      entityId: corporationId,
      ...(doc.expirationDate && {
        expirationDate: toIsoUtcFromDate(doc.expirationDate, "yyyy-MM-dd"),
      }),
    });

    if ("error" in result && result.error) {
      return { ok: false, code, error: result.error };
    }

    return { ok: true, code };
  } catch (error) {
    return { ok: false, code, error };
  }
};

/**
 * Création/remplacement “safe” d'un document externe via mutation GraphQL.
 *
 * - Ne throw jamais : retourne un résultat `{ ok: boolean }`.
 * - Permet un traitement homogène des erreurs côté appelant, au même titre que `safeUploadDocument`.
 */
const safeCreateOrReplaceExternalDocument = async (params: {
  code: string;
  corporationId: string;
  createOrReplaceExternalDocument: CreateOrReplaceExternalDocumentFn;
}): Promise<UploadDocumentResult> => {
  const { code, corporationId, createOrReplaceExternalDocument } = params;

  try {
    const result = await createOrReplaceExternalDocument({
      variables: {
        entityId: corporationId,
        documentTypeCode: code,
      },
    });

    if ("errors" in result && result.errors?.length) {
      return { ok: false, code, error: result.errors };
    }

    return { ok: true, code };
  } catch (error) {
    return { ok: false, code, error };
  }
};

/**
 * Upload batch de documents (internes + externes) avec un résultat par document.
 *
 * Comportement :
 * - Documents internes (upload fichier) :
 *   - upload si `doc.file` est présent ET `doc.existing` est faux
 * - Documents externes :
 *   - création/remplacement si `doc.existing` est vrai
 *
 * Retour :
 * - Tableau de résultats `{ ok: true/false, code, error? }` pour permettre à l'appelant
 *   d'afficher une liste des documents en erreur (toast) et/ou de poser des erreurs RHF par champ.
 */
export const uploadDocuments = async (
  documents: DocumentsMap,
  corporationId: string,
  postDocuments: PostDocumentsFn,
  createOrReplaceExternalDocument: CreateOrReplaceExternalDocumentFn
): Promise<UploadDocumentResult[]> => {
  const uploadTasks: Promise<UploadDocumentResult>[] = Object.entries(documents)
    .filter(([, doc]) => doc.file && !doc.existing)
    .map(([code, doc]) => safeUploadDocument({ doc, code, corporationId, postDocuments }));

  const externalTasks: Promise<UploadDocumentResult>[] = Object.entries(documents)
    .filter(([, doc]) => doc.existing)
    .map(([code]) =>
      safeCreateOrReplaceExternalDocument({
        code,
        corporationId,
        createOrReplaceExternalDocument,
      })
    );

  return Promise.all([...uploadTasks, ...externalTasks]);
};

/**
 * Upload “strict” d'un document unique (throw en cas d'erreur).
 *
 * À privilégier quand :
 * - on veut un comportement transactionnel (un échec doit stopper le flux)
 * - on gère l'erreur par un `try/catch` englobant (et pas un feedback par document)
 *
 * Remarque :
 * - Cette fonction conserve un comportement compatible avec un usage existant qui attend un throw.
 */
export const uploadDocument = async (
  doc: Document,
  code: string,
  corporationId: string,
  postDocuments: PostDocumentsFn
): Promise<UploadResult> => {
  const formData = new FormData();
  formData.append("file", doc.file!);

  const result = await postDocuments({
    documentTypeCode: code,
    data: formData,
    entityId: corporationId,
    ...(doc.expirationDate && {
      expirationDate: toIsoUtcFromDate(doc.expirationDate, "yyyy-MM-dd"),
    }),
  });

  if ("error" in result && result.error) {
    throw result.error;
  }

  return result;
};

/**
 * Suppression batch de documents supprimés dans le formulaire.
 *
 * Stratégie :
 * - On compare `defaultDocuments` (état initial) à `documents` (état courant du formulaire)
 * - Si un document avait `fileId` initialement, mais que le formulaire courant n'a plus de `fileId`,
 *   alors l'utilisateur a supprimé ce document -> on déclenche la suppression côté API.
 *
 * Cas gérés :
 * - `existing === true` : suppression via mutation `deleteExternalDocument`
 * - sinon : suppression via mutation `deleteDocument`
 *
 * Note :
 * - Cette fonction ne gère pas la suppression de documents ajoutés puis retirés avant sauvegarde
 *   (pas de fileId initial), ce qui est généralement OK car rien n'a été persisté côté API.
 */
export const deleteDocuments = async (
  documents: DocumentsMap,
  defaultDocuments: DefaultDocsMap,
  deleteDocument: DeleteDocumentFn,
  deleteExternalDocument: DeleteExternalDocumentFn
): Promise<void> => {
  const documentsToDelete = Object.entries(defaultDocuments)
    .filter(([key, defaultDoc]) => {
      const currentDoc = documents[key];
      return defaultDoc.fileId && !currentDoc?.fileId;
    })
    .map(([, defaultDoc]) => {
      return {
        fileId: defaultDoc.fileId,
        existing: defaultDoc.existing,
      };
    })
    .filter(({ fileId }) => fileId !== undefined);

  await Promise.all(
    documentsToDelete.map(({ fileId, existing }) => {
      if (existing) {
        deleteExternalDocument({
          variables: {
            documentId: fileId!,
          },
        });
      } else {
        deleteDocument({
          variables: {
            documentId: fileId!,
          },
        });
      }
    })
  );
};
